import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Loader from "../../components/Loader";
import Paginator from "../../components/Paginator";
import PropertiesTable from "../../components/PropertiesTable";
import ApiService from "../../services/ApiService";
import { toast } from 'react-toastify';
import { formatDate } from "../../utils/helper";
import { statusColor } from "../../assets/utils/statusColors";

const UserDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [isLoader, setIsLoader] = useState(false);
    const [user, setUser] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [stats, setStats] = useState({});
    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [tab, setTab] = useState('jobs');

    useEffect(() => {
        getUserDetails();
    }, [id, currentPage]);

    const getUserDetails = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: `users/${id}?page=${currentPage}&limit=10`,
            });
            const data = response.data;
            if (data.status) {
                setUser(data.data.user);
                setJobs(data.data.jobs);
                setStats(data.data.stats);
                setPagination(data.data.pagination);
            } else {
                toast.error(data.message);
                navigate('/users');
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
            toast.error('Failed to load user details');
            navigate('/users');
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

   

    if (isLoader && !user) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Loader text="Loading User Details..." size="40" />
            </div>
        );
    }

    if (!user) {
        return null;
    }

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Job Seeker Details
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                <Link to="/users" className="text-muted text-hover-primary">Job Seekers</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Details</li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/users" className="btn btn-sm btn-light-secondary">
                            <i className="bi bi-arrow-left me-1"></i>
                            Back to List
                        </Link>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    {/* User Profile Card */}
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-header cursor-pointer">
                            <div className="card-title m-0">
                                <h3 className="fw-bold m-0">User Profile</h3>
                            </div>
                        </div>
                        <div className="card-body p-9">
                            <div className="row mb-7">
                                <div className="col-lg-3 col-md-4 col-sm-6">
                                    <div className="d-flex flex-column align-items-center">
                                        <div className="symbol symbol-100px symbol-circle mb-7">
                                            <img src={user.picture} alt={user.name} />
                                        </div>
                                        <div className="text-center">
                                            <h3 className="fw-bold fs-4 mb-1">{user.name}</h3>
                                            <div className={`badge ${user.status ? 'badge-light-success' : 'badge-light-danger'} fs-7`}>
                                                {user.status ? 'Active' : 'Inactive'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-9 col-md-8 col-sm-6">
                                    <div className="row">
                                        <div className="col-lg-6">
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span className="required">Email</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">{user.email || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Phone</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">
                                                    {user.phone_code && user.phone ? `${user.phone_code} ${user.phone}` : 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Country</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">{user.country || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Registration Date</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">
                                                    {formatDate(user.createdAt, 'DD MMM YYYY, hh:mm A')}
                                                </span>
                                            </div>
                                        </div>
                                        {user.address && (
                                            <div className="col-lg-12">
                                                <div className="d-flex flex-column mb-7 fv-row">
                                                    <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                        <span>Address</span>
                                                    </label>
                                                    <span className="fs-6 text-gray-800">
                                                        {typeof user.address === 'object' 
                                                            ? Object.values(user.address).filter(Boolean).join(', ')
                                                            : user.address
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-5 fs-6">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${tab === 'jobs' ? 'active' : ''}`}
                                onClick={() => setTab('jobs')}
                            >
                                <i className="bi bi-calendar-check me-2"></i> Bookings
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${tab === 'properties' ? 'active' : ''}`}
                                onClick={() => setTab('properties')}
                            >
                                <i className="bi bi-houses me-2"></i> Properties
                            </button>
                        </li>
                    </ul>

                    {tab === 'properties' && (
                        <div className="card">
                            <div className="card-header border-0 pt-6">
                                <div className="card-title">
                                    <h3 className="fw-bold m-0">Properties</h3>
                                </div>
                            </div>
                            <div className="card-body pt-0">
                                <PropertiesTable userId={id} />
                            </div>
                        </div>
                    )}

                    {tab === 'jobs' && (<>
                    {/* Job Statistics */}
                    <div className="row g-5 g-xl-8 mb-5">
                        <div className="col-xl-3">
                            <div className="card bg-light-primary card-xl-stretch mb-xl-8">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <div className="text-muted fw-semibold fs-7">Total Bookings</div>
                                            <div className="fs-2 fw-bold text-primary">{stats.total_jobs || 0}</div>
                                        </div>
                                        <span className="svg-icon svg-icon-3x svg-icon-primary d-block my-2">
                                            <i className="bi bi-journals fs-1 text-primary"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3">
                            <div className="card bg-light-warning card-xl-stretch mb-xl-8">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <div className="text-muted fw-semibold fs-7">Requested</div>
                                            <div className="fs-2 fw-bold text-warning">{stats.requested_jobs || 0}</div>
                                        </div>
                                        <span className="svg-icon svg-icon-3x svg-icon-warning d-block my-2">
                                            <i className="bi bi-clock fs-1 text-warning"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3">
                            <div className="card bg-light-info card-xl-stretch mb-xl-8">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <div className="text-muted fw-semibold fs-7">Confirmed &amp; In-Progress</div>
                                            <div className="fs-2 fw-bold text-info">{stats.active_jobs || 0}</div>
                                        </div>
                                        <span className="svg-icon svg-icon-3x svg-icon-info d-block my-2">
                                            <i className="bi bi-play-circle fs-1 text-info"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3">
                            <div className="card bg-light-success card-xl-stretch mb-xl-8">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <div className="text-muted fw-semibold fs-7">Completed</div>
                                            <div className="fs-2 fw-bold text-success">{stats.completed_jobs || 0}</div>
                                        </div>
                                        <span className="svg-icon svg-icon-3x svg-icon-success d-block my-2">
                                            <i className="bi bi-check-circle fs-1 text-success"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Job History */}
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <h3 className="fw-bold m-0">Bookings</h3>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div className="table-responsive">
                                <table className="table align-middle table-hover fs-6 gy-5">
                                    <thead>
                                        <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                            <th>Job Code</th>
                                            <th>Service</th>
                                            <th>Location</th>
                                            <th>Date & Time</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Created Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="fw-semibold text-gray-600 fs-6">
                                        {jobs.length > 0 ? jobs.map((job, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <span className="fw-bold text-dark">{job.job_code}</span>
                                                </td>
                                                <td>
                                                    <div>
                                                        <div className="fw-bold text-dark">
                                                            {job.service_id?.name || 'N/A'}
                                                        </div>
                                                        {job.service_id?.category_id && (
                                                            <div className="text-muted fs-7">
{job.service_id.category_id.title}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="text-dark">{job.location || 'N/A'}</span>
                                                </td>
                                                <td>
                                                    <div>
                                                        <div className="fw-bold text-dark">
                                                            {job.date ? formatDate(job.date, 'DD MMM YYYY') : 'N/A'}
                                                        </div>
                                                        {job.time && (
                                                            <div className="text-muted fs-7">{job.time}</div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="fw-bold text-dark">
                                                        {job.pay_amount ? `$${job.pay_amount}` : 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge bg-${statusColor[job.status] || 'secondary'}`}>
                                                        {job.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    {formatDate(job.createdAt, 'DD MMM YYYY, hh:mm A')}
                                                </td>
                                                <td>
                                                    <Link 
                                                        to={`/bookings/${job._id}`} 
                                                        className="btn btn-sm btn-light-primary me-2"
                                                        title="View Booking"
                                                    >
                                                        <i className="bi bi-eye-fill"></i>
                                                    </Link>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="8" className="text-center text-muted py-8">
                                                    <div className="d-flex flex-column align-items-center">
                                                        <i className="bi bi-inbox fs-2 text-muted mb-3"></i>
                                                        <span>No bookings found for this user</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            
                            {isLoader && (
                                <div className="d-flex justify-content-center py-8">
                                    <Loader text="Loading jobs..." size="30" />
                                </div>
                            )}

                            {pagination.total_pages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-5">
                                    <div className="text-muted">
                                        Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                                        {Math.min(pagination.current_page * pagination.per_page, pagination.total_records)} of{' '}
                                        {pagination.total_records} jobs
                                    </div>
                                    <Paginator
                                        page={pagination.current_page}
                                        rows={pagination.per_page}
                                        totalRecords={pagination.total_records}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    </>)}
                </div>
            </div>
        </div>
    );
};

export default UserDetails;
